import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

/**
 * Fila plegable del panel de empresa, con la misma receta visual de `PlanAccordion` (aquel
 * está atado a la forma de un plan). Listas largas se leen como fichas que abren su detalle,
 * nunca como decenas de filas planas. El detalle solo se monta al abrir.
 */
function OrgExpandableRow({
  leading = null,
  title,
  subtitle = null,
  trailing = null,
  defaultExpanded = false,
  children = null
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const detailId = useId();

  return (
    <Box
      sx={{
        borderRadius: '14px',
        border: '1px solid',
        borderColor: open ? 'gold.soft' : 'hairline.main',
        backgroundColor: 'surface.raised',
        boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
        overflow: 'hidden',
        transition:
          'border-color 220ms cubic-bezier(0.32, 0.72, 0, 1), transform 220ms cubic-bezier(0.32, 0.72, 0, 1), background-color 220ms cubic-bezier(0.32, 0.72, 0, 1)',
        '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' }
      }}>
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        spacing={1.5}
        aria-expanded={open}
        aria-controls={detailId}
        onClick={() => setOpen((previous) => !previous)}
        sx={{
          width: '100%',
          px: 2,
          py: 1.6,
          border: 0,
          background: 'none',
          textAlign: 'left',
          color: 'inherit',
          font: 'inherit',
          cursor: 'pointer',
          transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1)',
          '&:active': { transform: 'scale(0.98)' },
          '&:focus-visible': { outline: '2px solid var(--otx-focus-ring)', outlineOffset: -2 }
        }}>
        {leading}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontWeight: 700, color: 'text.primary' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {trailing}
        <ExpandMore
          sx={{
            flex: 'none',
            color: 'text.disabled',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)'
          }}
        />
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Box id={detailId} sx={{ px: 2, pb: 2, pt: 0.5, borderTop: '1px solid', borderColor: 'hairline.main' }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

OrgExpandableRow.propTypes = {
  leading: PropTypes.node,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  trailing: PropTypes.node,
  defaultExpanded: PropTypes.bool,
  children: PropTypes.node
};

export default OrgExpandableRow;
